import React, { useState, useEffect, useRef } from 'react';
import { studentAPI, dynamicOptionsAPI } from '../services/api';
import { useSelector } from 'react-redux';
import { Form, Dropdown, Modal, Collapse, Pagination } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import {
  Search, InfoCircleFill, PencilSquare, Trash,
  ExclamationTriangleFill, CheckCircleFill, Eye, EyeSlash, Printer, BarChartFill, EyeFill
} from 'react-bootstrap-icons';
import '../CSS/Students.css';
import ExportStudentsDropdown from '../Components/ExportStudentsDropdown';
import CoursesIcon from '../img/course.gif';
import StudentGuideModal from '../Components/StudentGuideModal';
import PrintStudentsModal from '../Components/PrintStudentsModal';
import PrintClassModal from '../Components/PrintClassModal';

/* ─────────────────────────────────────────
   SearchableSelect sub-component
───────────────────────────────────────── */
const SearchableSelect = ({ name, value, onChange, options, placeholder, isInvalid, onOpenChange, onNewOption }) => {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const setOpenWithCallback = (val) => { setOpen(val); onOpenChange?.(val); };
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpenWithCallback(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filtered = options.filter(o => o.toLowerCase().includes(search.toLowerCase()));
  const trimmed = search.trim();
  const isNew = trimmed && !options.some(o => o.toLowerCase() === trimmed.toLowerCase());

  const handleSelect = (option) => {
    onChange({ target: { name, value: option } });
    setSearch('');
    setOpenWithCallback(false);
  };

  const handleAddNew = () => {
    handleSelect(trimmed);
    onNewOption?.(trimmed); // fire-and-forget: saves to DB immediately
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <input
        type="text"
        className={`stu-form-input${isInvalid ? ' stu-input-invalid' : ''}`}
        placeholder=" "
        value={open ? search : value}
        onFocus={() => setOpenWithCallback(true)}
        onChange={(e) => { setSearch(e.target.value); setOpenWithCallback(true); }}
        autoComplete="off"
        data-has-value={value ? 'true' : 'false'}
      />
      {open && (
        <div className="stu-searchable-dropdown">
          {isNew && (
            <div
              onMouseDown={handleAddNew}
              className="stu-searchable-option stu-searchable-option-new"
              style={{ fontStyle: 'italic', color: '#0d6efd' }}
            >
              + Add &ldquo;{trimmed}&rdquo;
            </div>
          )}
          {filtered.length === 0 && !isNew
            ? <div className="stu-searchable-no-result">No results</div>
            : filtered.map(option => (
              <div
                key={option}
                onMouseDown={() => handleSelect(option)}
                className={`stu-searchable-option${option === value ? ' stu-searchable-option-selected' : ''}`}
              >
                {option}
              </div>
            ))
          }
        </div>
      )}
    </div>
  );
};
/* ─────────────────────────────────────────
   Floating label field wrapper
   Defined OUTSIDE Students to prevent remount on every keystroke
───────────────────────────────────────── */
const FormField = ({ label, error, isActive, children }) => (
  <div className="stu-field-group">
    {children}
    <label className={`stu-field-label${isActive ? ' stu-field-label--float' : ''}`}>{label}</label>
    {error && <div className="stu-field-error">{error}</div>}
  </div>
);

/* ─────────────────────────────────────────
   DatePicker — three-dropdown date input
   Works consistently on mobile and desktop
───────────────────────────────────────── */
const DatePicker = ({ name, value, onChange, isInvalid, minYear, maxYear }) => {
  // Derive initial parts from the incoming value (YYYY-MM-DD)
  const fromValue = (v) => {
    const parts = v ? v.split('-') : ['', '', ''];
    return {
      year:  parts[0] || '',
      month: parts[1] || '',
      day:   parts[2] || '',
    };
  };

  const [parts, setParts] = React.useState(() => fromValue(value));

  // Sync internal state when value is externally reset (e.g. form clear)
  // Only sync inward when:
// 1. value is a complete YYYY-MM-DD date (edit mode pre-fill, or form reset after submit)
// 2. value is explicitly empty AND all internal parts are also empty (genuine form clear)
React.useEffect(() => {
  const isCompleteDate = /^\d{4}-\d{2}-\d{2}$/.test(value);
  const isGenuineReset = !value && !parts.year && !parts.month && !parts.day;

  if (isCompleteDate) {
    setParts(fromValue(value));
  } else if (isGenuineReset) {
    setParts({ year: '', month: '', day: '' });
  }
  // If value is '' but parts have selections, do nothing — user is mid-selection
}, [value]);

  const months = [
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' },
  ];

  const daysInMonth = parts.year && parts.month
    ? new Date(parseInt(parts.year), parseInt(parts.month), 0).getDate()
    : 31;

  const days = Array.from({ length: daysInMonth }, (_, i) =>
    String(i + 1).padStart(2, '0')
  );

  const currentYear = new Date().getFullYear();
  const yearFrom = minYear || currentYear - 100;
  const yearTo   = maxYear || currentYear;
  const years = Array.from(
    { length: yearTo - yearFrom + 1 },
    (_, i) => String(yearTo - i)
  );

  const handlePartChange = (part, val) => {
    const newParts = { ...parts, [part]: val };
    setParts(newParts); // ← always keep the selection in local state

    const { year: y, month: m, day: d } = newParts;

    if (y && m && d) {
      // All three filled — clamp day if needed and emit full date
      const maxDays = new Date(parseInt(y), parseInt(m), 0).getDate();
      const clampedDay = parseInt(d) > maxDays
        ? String(maxDays).padStart(2, '0')
        : d;
      onChange({ target: { name, value: `${y}-${m}-${clampedDay}` } });
    } else {
      // Not all filled yet — emit empty so validation knows it's incomplete
      // but do NOT wipe local state (selections stay visible)
      onChange({ target: { name, value: '' } });
    }
  };

  const selectClass = `stu-form-input stu-date-select${isInvalid ? ' stu-input-invalid' : ''}`;

  return (
    <div className="stu-date-picker">
      {/* Day */}
      <select
        className={selectClass}
        value={parts.day}
        onChange={(e) => handlePartChange('day', e.target.value)}
      >
        <option value="">Day</option>
        {days.map(d => <option key={d} value={d}>{parseInt(d)}</option>)}
      </select>

      {/* Month */}
      <select
        className={selectClass}
        value={parts.month}
        onChange={(e) => handlePartChange('month', e.target.value)}
      >
        <option value="">Month</option>
        {months.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
      </select>

      {/* Year */}
      <select
        className={selectClass}
        value={parts.year}
        onChange={(e) => handlePartChange('year', e.target.value)}
      >
        <option value="">Year</option>
        {years.map(y => <option key={y} value={y}>{y}</option>)}
      </select>
    </div>
  );
};
/* ─────────────────────────────────────────
   Main Students component
───────────────────────────────────────── */
const Students = () => {
  const { user } = useSelector(state => state.auth);
  const userRole = user?.role || '';

  const [exporting, setExporting] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [showConstituencyPrintModal, setShowConstituencyPrintModal] = useState(false);
  const [showRegionPrintModal, setShowRegionPrintModal] = useState(false);
  const [showReligionPrintModal, setShowReligionPrintModal] = useState(false);

  const [students, setStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const [sortCriteria, setSortCriteria] = useState('studentName');
  const [sortOrder, setSortOrder] = useState('asc');

  const [showPassword, setShowPassword] = useState(false);
  const [studentData, setStudentData] = useState({
    studentName: '', dateOfBirth: '', gender: '', lastSchoolAttended: '',
    province: '', religion: '', region: '', district: '', constituency: '',
    village: '', class: '', phoneNumber: '', fatherName: '', motherName: '',
    parentsPhoneNumber: '', fathersOccupation: '', mothersOccupation: '',
    password: '', email: '', registrationDate: ''
  });

  const [showStats, setShowStats] = useState(false);
  const [statistics, setStatistics] = useState({
    totalStudents: 0, maleStudents: 0, femaleStudents: 0, classStats: {}
  });

  const [editingStudentId, setEditingStudentId] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });
  const [validationErrors, setValidationErrors] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [studentToDelete, setStudentToDelete] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 75, total: 0, totalPages: 1 });

  const formRef = useRef(null);
  const [provinceOpen, setProvinceOpen] = useState(false);
  const [regionOpen, setRegionOpen] = useState(false);
  const [religionOpen, setReligionOpen] = useState(false);
  const [constituencyOpen, setConstituencyOpen] = useState(false);

  const [formResetKey, setFormResetKey] = useState(0);

  const [useIdAsPassword, setUseIdAsPassword] = useState(false);
  const [editingStudentName, setEditingStudentName] = useState('');
  const [selectedStudent, setSelectedStudent] = useState(null);

  /* ── Options ── */

const classOptions = ['6', '7', '8', '9', '10', '11', '12'];
const [provinceOptions, setProvinceOptions] = useState([]);
const [regionOptions, setRegionOptions] = useState([]);
const [religionOptions, setReligionOptions] = useState([]);
const [constituencyOptions, setConstituencyOptions] = useState([]);

useEffect(() => {
  const load = async (fieldName, setter) => {
    try {
      const res = await dynamicOptionsAPI.getOptions(fieldName);
      setter(res.data.options);
    } catch {
      // silently fall back to empty; existing values still show in the input
    }
  };
  load('province', setProvinceOptions);
  load('region', setRegionOptions);
  load('religion', setReligionOptions);
  load('constituency', setConstituencyOptions);
}, []);
  const [inlineEdit, setInlineEdit] = useState({ field: null, value: '' });

  /* ── Effects ── */
  useEffect(() => { fetchStudents(); }, [sortCriteria, sortOrder, pagination.page, pagination.limit, searchTerm]);

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearchTerm(searchTerm), 500);
    return () => clearTimeout(id);
  }, [searchTerm]);

  useEffect(() => { fetchStudents(); }, [debouncedSearchTerm, pagination.page, pagination.limit]);

  useEffect(() => {
    if (toast.show) {
      const id = setTimeout(() => setToast(prev => ({ ...prev, show: false })), 3000);
      return () => clearTimeout(id);
    }
  }, [toast.show]);

 

  /* ── API calls ── */

  const fetchStudents = async (isLoadMore = false) => {
    const abortController = new AbortController();
    try {
      isLoadMore ? setIsLoadingMore(true) : setLoading(true);
      const response = await studentAPI.getStudents({
        page: pagination.page, limit: pagination.limit,
        search: searchTerm.trim(), sortBy: sortCriteria,
        sortOrder, signal: abortController.signal
      });
      if (isLoadMore) {
        setStudents(prev => [...prev, ...response.data.data]);
      } else {
        setStudents(response.data.data);
      }
      setPagination(prev => ({ ...prev, total: response.data.total, totalPages: response.data.totalPages }));
    } catch (error) {
      if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') return;
      showNotification(error.response?.data?.error || 'Error fetching students', 'danger');
    } finally {
      isLoadMore ? setIsLoadingMore(false) : setLoading(false);
    }
    return () => abortController.abort();
  };

  const fetchStatistics = async () => {
    try {
      const response = await studentAPI.getStatistics();
      return response.data;
    } catch (error) {
      showNotification('Error fetching statistics', 'danger');
      return null;
    }
  };

  /* ── Helpers ── */
  const showNotification = (message, variant) => {
    setToast({ show: true, message, variant });
  };

const handleUseIdAsPassword = (e) => {
  const checked = e.target.checked;
  setUseIdAsPassword(checked);
  // Clear the password field when unchecking, leave it empty when checking.
  // The actual password will be set by the backend using the real generated ID.
  if (!checked) {
    setStudentData(prev => ({ ...prev, password: '' }));
  }
};

  const handleChange = (e) => {
  const { name, value } = e.target;
  if (name === 'password' && useIdAsPassword) {
    setUseIdAsPassword(false); 
  }
  setStudentData(prev => ({ ...prev, [name]: value }));
};

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleSortChange = (criteria, order) => {
    setSortCriteria(criteria);
    setSortOrder(order);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (page) => setPagination(prev => ({ ...prev, page }));

  const handleDelete = (student) => { setStudentToDelete(student); setShowDeleteModal(true); };

  const confirmDelete = async () => {
    try {
      await studentAPI.deleteStudent(studentToDelete.id);
      showNotification('Student deleted successfully', 'success');
      fetchStudents();
      setShowDeleteModal(false);
    } catch {
      showNotification('Error deleting student', 'danger');
    }
  };

  const handleEdit = (student) => {
    setStudentData({
      studentName: student.studentName || '',
      dateOfBirth: student.dateOfBirth
      ? new Date(student.dateOfBirth).toLocaleDateString('en-CA')
      : '',
      gender: student.gender || '',
      lastSchoolAttended: student.lastSchoolAttended || '',
      province: student.province || '',
      region: student.region || '',
      religion: student.religion || '',
      district: student.district || '',
      constituency: student.constituency || '',
      village: student.village || '',
      class: student.class || '',
      phoneNumber: student.phoneNumber || '',
      fatherName: student.fatherName || '',
      motherName: student.motherName || '',
      parentsPhoneNumber: student.parentsPhoneNumber || '',
      fathersOccupation: student.fathersOccupation || '',
      mothersOccupation: student.mothersOccupation || '',
      password: '',
      email: student.email || '',
      registrationDate: student.registrationDate
  ? new Date(student.registrationDate).toLocaleDateString('en-CA')
  : ''
    });
    setEditingStudentId(student.id);
    setEditingStudentName(student.studentName || ''); 
    setUseIdAsPassword(false);                            // ← always reset toggle on edit open
    formRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleShowStats = async () => {
    if (!showStats) {
      const stats = await fetchStatistics();
      if (stats) setStatistics(stats);
    }
    setShowStats(!showStats);
  };


const handleInlineSave = async (field) => {
  try {
    await studentAPI.patch(selectedStudent.id, { [field]: inlineEdit.value });
    const updated = { ...selectedStudent, [field]: inlineEdit.value };
    setSelectedStudent(updated);
    setStudents(prev => prev.map(s => s.id === selectedStudent.id ? updated : s));
    setInlineEdit({ field: null, value: '' });
    showNotification('Field updated successfully', 'success');
  } catch {
    showNotification('Failed to update field', 'danger');
  }
};
  /* ── Validation ── */
  const validateForm = () => {
    const errors = {};
    const namePattern = /^[A-Za-z\s]+$/;
    const phonePattern = /^\d{8,}$/;
    const occupationPattern = /^[A-Za-z\s]+$/;
    const passwordPattern = /^[A-Za-z0-9@#*&-]{8,}$/;
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!studentData.email?.trim()) errors.email = 'Email is required.';
    else if (!studentData.email.match(emailPattern)) errors.email = 'Please enter a valid email address.';
    if (!studentData.registrationDate) errors.registrationDate = 'Registration date is required.';
    if (!studentData.studentName?.trim()) errors.studentName = 'Student name is required.';
    else if (!studentData.studentName.match(namePattern)) errors.studentName = 'Name should contain only letters and spaces.';
    if (!studentData.dateOfBirth) errors.dateOfBirth = 'Date of Birth is required.';
    if (!studentData.gender) errors.gender = 'Gender is required.';
    else if (!['Male', 'Female'].includes(studentData.gender)) errors.gender = 'Gender must be Male or Female';
    if (!studentData.lastSchoolAttended?.trim()) errors.lastSchoolAttended = 'Last school attended is required.';
    else if (!studentData.lastSchoolAttended.match(namePattern)) errors.lastSchoolAttended = 'School name should contain only letters and spaces.';
    if (!studentData.province) errors.province = 'Province is required.';
    if (!studentData.region) errors.region = 'Region is required.';
    if (!studentData.religion) errors.religion = 'Religion is required.';
    if (!studentData.constituency) errors.constituency = 'Constituency is required.';
    if (!studentData.district?.trim()) errors.district = 'District is required.';
    else if (!studentData.district.match(namePattern)) errors.district = 'District should contain only letters and spaces.';
    if (!studentData.village?.trim()) errors.village = 'Village is required.';
    else if (!studentData.village.match(namePattern)) errors.village = 'Village should contain only letters and spaces.';
    if (!studentData.class) errors.class = 'Class is required.';
    if (!studentData.phoneNumber?.trim()) errors.phoneNumber = 'Phone number is required.';
    else if (!studentData.phoneNumber.match(phonePattern)) errors.phoneNumber = 'Phone number must be at least 8 digits.';
    if (!studentData.fatherName?.trim()) errors.fatherName = "Father's name is required.";
    else if (!studentData.fatherName.match(namePattern)) errors.fatherName = "Father's name should contain only letters and spaces.";
    if (!studentData.motherName?.trim()) errors.motherName = "Mother's name is required.";
    else if (!studentData.motherName.match(namePattern)) errors.motherName = "Mother's name should contain only letters and spaces.";
    if (!studentData.fathersOccupation?.trim()) errors.fathersOccupation = "Father's occupation is required.";
    else if (!studentData.fathersOccupation.match(occupationPattern)) errors.fathersOccupation = "Father's occupation should contain only letters and spaces.";
    if (!studentData.mothersOccupation?.trim()) errors.mothersOccupation = "Mother's occupation is required.";
    else if (!studentData.mothersOccupation.match(occupationPattern)) errors.mothersOccupation = "Mother's occupation should contain only letters and spaces.";
    if (!studentData.parentsPhoneNumber?.trim()) errors.parentsPhoneNumber = 'Parents phone number is required.';
    else if (!studentData.parentsPhoneNumber.match(phonePattern)) errors.parentsPhoneNumber = 'Parents phone number must be at least 8 digits.';
   if (!useIdAsPassword) {
  if (!studentData.password?.trim()) errors.password = 'Password is required.';
  else if (!studentData.password.match(passwordPattern)) errors.password = 'Password must be at least 8 characters (letters, numbers, @#*&-).';
  }
    return errors;
  };

  const isDuplicateName = (name) =>
    students.some(s => s.studentName?.trim().toLowerCase() === name?.trim().toLowerCase() && (!editingStudentId || s.id !== editingStudentId));

 const handleSubmit = async (e) => {
  e.preventDefault();
  const errors = validateForm();
  if (Object.keys(errors).length > 0) {
    setValidationErrors(errors);
    showNotification('Please correct the highlighted errors.', 'danger');
    return;
  }
  if (isDuplicateName(studentData.studentName)) {
    showNotification('Student Name already exists', 'danger');
    return;
  }
  try {
    if (editingStudentId) {
      await studentAPI.updateStudent(editingStudentId, {
        ...studentData,
        useIdAsPassword
      });
      showNotification('Student updated successfully', 'success');
    } else {
      // Send useIdAsPassword flag to backend so it can set the correct password
      await studentAPI.addStudent({ ...studentData, useIdAsPassword });
      showNotification('Student added successfully', 'success');
    }

    setValidationErrors({});
    fetchStudents();

    // IMPORTANT: Reset useIdAsPassword FIRST before fetching the next ID.
    // This prevents fetchNextStudentId from repopulating the password field.
    setUseIdAsPassword(false);
    setEditingStudentId(null);
    setEditingStudentName('');
    setFormResetKey(prev => prev + 1); 

    // Reset the form with empty password — the password field must always be blank after submit
    setStudentData({
      studentName: '', dateOfBirth: '', gender: '', lastSchoolAttended: '',
      province: '', region: '', religion: '', district: '', constituency: '',
      village: '', class: '', phoneNumber: '', fatherName: '', motherName: '',
      parentsPhoneNumber: '', fathersOccupation: '', mothersOccupation: '',
      password: '', email: '', registrationDate: ''
    });

  
  } catch {
    showNotification(editingStudentId ? 'Error updating student' : 'Error adding student', 'danger');
  }
};
  /* ── Sub-renders ── */
  const renderSortDropdown = () => (
    <Dropdown className="stu-sort-dropdown">
      <Dropdown.Toggle id="stu-sort-dropdown" className="stu-sort-toggle">
        Sort: {sortCriteria} ({sortOrder})
      </Dropdown.Toggle>
      <Dropdown.Menu className="stu-sort-menu">
        <Dropdown.Header className="stu-sort-header">Name</Dropdown.Header>
        <Dropdown.Item className="stu-sort-item" onClick={() => handleSortChange('studentName', 'ASC')}>A → Z</Dropdown.Item>
        <Dropdown.Item className="stu-sort-item" onClick={() => handleSortChange('studentName', 'DESC')}>Z → A</Dropdown.Item>
        <Dropdown.Divider />
        <Dropdown.Header className="stu-sort-header">Date of Birth</Dropdown.Header>
        <Dropdown.Item className="stu-sort-item" onClick={() => handleSortChange('dateOfBirth', 'ASC')}>Oldest First</Dropdown.Item>
        <Dropdown.Item className="stu-sort-item" onClick={() => handleSortChange('dateOfBirth', 'DESC')}>Newest First</Dropdown.Item>
        <Dropdown.Divider />
        <Dropdown.Header className="stu-sort-header">Class</Dropdown.Header>
        <Dropdown.Item className="stu-sort-item" onClick={() => handleSortChange('class', 'ASC')}>Class 6 → 12</Dropdown.Item>
        <Dropdown.Item className="stu-sort-item" onClick={() => handleSortChange('class', 'DESC')}>Class 12 → 6</Dropdown.Item>
        <Dropdown.Divider />
        <Dropdown.Header className="stu-sort-header">Registration Date</Dropdown.Header>
        <Dropdown.Item className="stu-sort-item" onClick={() => handleSortChange('registrationDate', 'ASC')}>Oldest First</Dropdown.Item>
        <Dropdown.Item className="stu-sort-item" onClick={() => handleSortChange('registrationDate', 'DESC')}>Newest First</Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  );

  const renderPagination = () => {
    const { page, totalPages, total } = pagination;
    const pages = Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
      if (totalPages <= 5) return i + 1;
      if (page <= 3) return i + 1;
      if (page >= totalPages - 2) return totalPages - 4 + i;
      return page - 2 + i;
    });
    return (
      <div className="stu-pagination-wrapper">
        <div className="stu-pagination-info">
          {students.length === 0 ? 'No records found' : `Page ${page} of ${totalPages} · Total: ${total}`}
        </div>
        <nav>
          <ul className="stu-pagination">
            <li className={`stu-page-item${page === 1 ? ' stu-disabled' : ''}`}>
              <button className="stu-page-link" onClick={() => handlePageChange(1)} disabled={page === 1}>First</button>
            </li>
            <li className={`stu-page-item${page === 1 ? ' stu-disabled' : ''}`}>
              <button className="stu-page-link" onClick={() => handlePageChange(page - 1)} disabled={page === 1}>Prev</button>
            </li>
            {pages.map(p => (
              <li key={p} className={`stu-page-item${page === p ? ' stu-active' : ''}`}>
                <button className="stu-page-link" onClick={() => handlePageChange(p)}>{p}</button>
              </li>
            ))}
            <li className={`stu-page-item${page === totalPages ? ' stu-disabled' : ''}`}>
              <button className="stu-page-link" onClick={() => handlePageChange(page + 1)} disabled={page === totalPages}>Next</button>
            </li>
            <li className={`stu-page-item${page === totalPages ? ' stu-disabled' : ''}`}>
              <button className="stu-page-link" onClick={() => handlePageChange(totalPages)} disabled={page === totalPages}>Last</button>
            </li>
          </ul>
        </nav>
      </div>
    );
  };

  /* ── Stat helpers ── */
  const renderStudentList = (namesString) => {
    const list = namesString ? namesString.split(', ') : [];
    return list.length > 0
      ? <ul className="stu-student-list">{list.map((s, i) => <li key={i} className="stu-student-list-item">{s}</li>)}</ul>
      : <div className="stu-empty-text">No students</div>;
  };

 

  /* ═══════════════════════════════════════
     RENDER
  ═══════════════════════════════════════ */
  return (
    <>
     <div className="stu-page">
      {/* ── Header ── */}
   <div className="row my-4">
  <div className="col-12">
    <h3>
      Students
      <img className="animated-icons" src={CoursesIcon} alt="Students icon" />
    </h3>
  </div>
</div>

      {/* ── Main content ── */}
      <div className="stu-main-container">
        <div className="stu-main-content">

          {userRole === 'staff' && (
            <div className="stu-staff-alert">
              <InfoCircleFill className="stu-alert-icon" />
              You have view-only access as a staff member.
            </div>
          )}

          {/* Search */}
          <div className="stu-search-wrapper">
            <Search className="stu-search-icon" />
            <input
              type="text"
              className="stu-search-input"
              placeholder="Search Students..."
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </div>

          {/* Table section */}
          <h4 className="stu-section-title">Students List</h4>

          <div className="stu-form-card">
            {renderSortDropdown()}
          </div>

          <div className="stu-table-container">
            <div className="stu-list-drawer-layout">

              {/* ── Card List ── */}
              <div className="stu-card-list-pane">
                {loading ? (
                  <div className="stu-loading-state">
                    <div className="stu-loader-wrap">
                      <div className="stu-loader-ring" />
                      <span className="stu-loader-text">Loading Students</span>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="stu-card-list-header">
                      <span className="stu-card-col stu-col-id">Student ID</span>
                      <span className="stu-card-col stu-col-name">Name</span>
                      <span className="stu-card-col stu-col-class">Class</span>
                      <span className="stu-card-col stu-col-gender">Gender</span>
                      <span className="stu-card-col stu-col-phone">Phone</span>
                      <span className="stu-card-col stu-col-chevron"></span>
                    </div>

                    {students.length === 0 ? (
                      <div className="stu-empty-state">
                        <InfoCircleFill className="stu-empty-icon" /> No results found
                      </div>
                    ) : (
                      students.map((student, index) => (
                        <div
                          key={index}
                          className={`stu-student-row${selectedStudent?.id === student.id ? ' stu-student-row--active' : ''}`}
                          onClick={() => setSelectedStudent(student)}
                        >
                          <span className="stu-card-col stu-col-id">
                            <span className="stu-id-badge">{student.studentIdNumber || '—'}</span>
                          </span>
                          <span className="stu-card-col stu-col-name stu-cell-name">{student.studentName}</span>
                          <span className="stu-card-col stu-col-class">{student.class}</span>
                          <span className="stu-card-col stu-col-gender">
                            <span className={`stu-gender-badge stu-gender-${student.gender === 'Male' ? 'm' : 'f'}`}>
                              {student.gender === 'Male' ? 'M' : 'F'}
                            </span>
                          </span>
                          <span className="stu-card-col stu-col-phone">{student.phoneNumber}</span>
                          <span className="stu-card-col stu-col-chevron">›</span>
                        </div>
                      ))
                    )}
                  </>
                )}
              </div>

              {/* ── Detail Drawer ── */}
              {selectedStudent && (
                <div className="stu-detail-drawer">
                  <div className="stu-drawer-header">
                    <div className="stu-drawer-avatar">
                      {selectedStudent.studentName?.split(' ').map(n => n[0]).slice(0, 2).join('')}
                    </div>
                    <div className="stu-drawer-identity">
                      <div className="stu-drawer-name">{selectedStudent.studentName}</div>
                      <div className="stu-drawer-sub">{selectedStudent.studentIdNumber} · Grade {selectedStudent.class}</div>
                    </div>
                    <button className="stu-drawer-close" onClick={() => setSelectedStudent(null)}>✕</button>
                  </div>

                  {userRole === 'admin' && (
                    <div className="stu-drawer-actions">
                      <button className="stu-drawer-btn stu-drawer-btn-edit" onClick={() => { handleEdit(selectedStudent); setSelectedStudent(null); }}>
                        <PencilSquare /> Edit
                      </button>
                      <button className="stu-drawer-btn stu-drawer-btn-delete" onClick={() => { handleDelete(selectedStudent); setSelectedStudent(null); }}>
                        <Trash /> Delete
                      </button>
                    </div>
                  )}

                  <div className="stu-drawer-body">

  {/* ── Personal ── */}
  <div className="stu-drawer-section-label">Personal</div>

  <div className="stu-drawer-row">
    <span>Email</span>
    {userRole === 'admin' && inlineEdit.field === 'email' ? (
      <span className="stu-inline-edit-active">
        <input className="stu-inline-input" value={inlineEdit.value} onChange={e => setInlineEdit(p => ({ ...p, value: e.target.value }))} />
        <button className="stu-inline-save" onClick={() => handleInlineSave('email')}>Save</button>
        <button className="stu-inline-cancel" onClick={() => setInlineEdit({ field: null, value: '' })}>Cancel</button>
      </span>
    ) : (
      <span className="stu-inline-edit-value">
        {selectedStudent.email || 'N/A'}
        {userRole === 'admin' && <PencilSquare className="stu-inline-pencil" onClick={() => setInlineEdit({ field: 'email', value: selectedStudent.email || '' })} />}
      </span>
    )}
  </div>

  <div className="stu-drawer-row">
    <span>Phone</span>
    {userRole === 'admin' && inlineEdit.field === 'phoneNumber' ? (
      <span className="stu-inline-edit-active">
        <input className="stu-inline-input" value={inlineEdit.value} onChange={e => setInlineEdit(p => ({ ...p, value: e.target.value }))} />
        <button className="stu-inline-save" onClick={() => handleInlineSave('phoneNumber')}>Save</button>
        <button className="stu-inline-cancel" onClick={() => setInlineEdit({ field: null, value: '' })}>Cancel</button>
      </span>
    ) : (
      <span className="stu-inline-edit-value">
        {selectedStudent.phoneNumber || 'N/A'}
        {userRole === 'admin' && <PencilSquare className="stu-inline-pencil" onClick={() => setInlineEdit({ field: 'phoneNumber', value: selectedStudent.phoneNumber || '' })} />}
      </span>
    )}
  </div>

  <div className="stu-drawer-row">
    <span>Date of Birth</span>
    {userRole === 'admin' && inlineEdit.field === 'dateOfBirth' ? (
      <span className="stu-inline-edit-active">
        <DatePicker
          name="dateOfBirth"
          value={inlineEdit.value}
          onChange={e => setInlineEdit(p => ({ ...p, value: e.target.value }))}
          maxYear={new Date().getFullYear()}
        />
        <button className="stu-inline-save" onClick={() => handleInlineSave('dateOfBirth')}>Save</button>
        <button className="stu-inline-cancel" onClick={() => setInlineEdit({ field: null, value: '' })}>Cancel</button>
      </span>
    ) : (
      <span className="stu-inline-edit-value">
        {selectedStudent.dateOfBirth ? new Date(selectedStudent.dateOfBirth).toLocaleDateString() : 'N/A'}
        {userRole === 'admin' && <PencilSquare className="stu-inline-pencil" onClick={() => setInlineEdit({ field: 'dateOfBirth', value: selectedStudent.dateOfBirth?.split('T')[0] || '' })} />}
      </span>
    )}
  </div>

  <div className="stu-drawer-row">
    <span>Gender</span>
    {userRole === 'admin' && inlineEdit.field === 'gender' ? (
      <span className="stu-inline-edit-active">
        <select className="stu-inline-input" value={inlineEdit.value} onChange={e => setInlineEdit(p => ({ ...p, value: e.target.value }))}>
          <option value="">Select</option>
          <option value="Male">Male</option>
          <option value="Female">Female</option>
        </select>
        <button className="stu-inline-save" onClick={() => handleInlineSave('gender')}>Save</button>
        <button className="stu-inline-cancel" onClick={() => setInlineEdit({ field: null, value: '' })}>Cancel</button>
      </span>
    ) : (
      <span className="stu-inline-edit-value">
        {selectedStudent.gender || 'N/A'}
        {userRole === 'admin' && <PencilSquare className="stu-inline-pencil" onClick={() => setInlineEdit({ field: 'gender', value: selectedStudent.gender || '' })} />}
      </span>
    )}
  </div>

  <div className="stu-drawer-row">
    <span>Religion</span>
    {userRole === 'admin' && inlineEdit.field === 'religion' ? (
      <span className="stu-inline-edit-active">
        <select className="stu-inline-input" value={inlineEdit.value} onChange={e => setInlineEdit(p => ({ ...p, value: e.target.value }))}>
          <option value="">Select</option>
          {religionOptions.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        <button className="stu-inline-save" onClick={() => handleInlineSave('religion')}>Save</button>
        <button className="stu-inline-cancel" onClick={() => setInlineEdit({ field: null, value: '' })}>Cancel</button>
      </span>
    ) : (
      <span className="stu-inline-edit-value">
        {selectedStudent.religion || 'N/A'}
        {userRole === 'admin' && <PencilSquare className="stu-inline-pencil" onClick={() => setInlineEdit({ field: 'religion', value: selectedStudent.religion || '' })} />}
      </span>
    )}
  </div>

  <div className="stu-drawer-row">
    <span>Last School</span>
    {userRole === 'admin' && inlineEdit.field === 'lastSchoolAttended' ? (
      <span className="stu-inline-edit-active">
        <input className="stu-inline-input" value={inlineEdit.value} onChange={e => setInlineEdit(p => ({ ...p, value: e.target.value }))} />
        <button className="stu-inline-save" onClick={() => handleInlineSave('lastSchoolAttended')}>Save</button>
        <button className="stu-inline-cancel" onClick={() => setInlineEdit({ field: null, value: '' })}>Cancel</button>
      </span>
    ) : (
      <span className="stu-inline-edit-value">
        {selectedStudent.lastSchoolAttended || 'N/A'}
        {userRole === 'admin' && <PencilSquare className="stu-inline-pencil" onClick={() => setInlineEdit({ field: 'lastSchoolAttended', value: selectedStudent.lastSchoolAttended || '' })} />}
      </span>
    )}
  </div>

  <div className="stu-drawer-row">
    <span>Class</span>
    {userRole === 'admin' && inlineEdit.field === 'class' ? (
      <span className="stu-inline-edit-active">
        <select className="stu-inline-input" value={inlineEdit.value} onChange={e => setInlineEdit(p => ({ ...p, value: e.target.value }))}>
          <option value="">Select</option>
          {classOptions.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <button className="stu-inline-save" onClick={() => handleInlineSave('class')}>Save</button>
        <button className="stu-inline-cancel" onClick={() => setInlineEdit({ field: null, value: '' })}>Cancel</button>
      </span>
    ) : (
      <span className="stu-inline-edit-value">
        {selectedStudent.class || 'N/A'}
        {userRole === 'admin' && <PencilSquare className="stu-inline-pencil" onClick={() => setInlineEdit({ field: 'class', value: selectedStudent.class || '' })} />}
      </span>
    )}
  </div>

  <div className="stu-drawer-row">
    <span>Reg. Date</span>
    {userRole === 'admin' && inlineEdit.field === 'registrationDate' ? (
      <span className="stu-inline-edit-active">
       <DatePicker
        name="registrationDate"
        value={inlineEdit.value}
        onChange={e => setInlineEdit(p => ({ ...p, value: e.target.value }))}
        maxYear={new Date().getFullYear()}
      />
        <button className="stu-inline-save" onClick={() => handleInlineSave('registrationDate')}>Save</button>
        <button className="stu-inline-cancel" onClick={() => setInlineEdit({ field: null, value: '' })}>Cancel</button>
      </span>
    ) : (
      <span className="stu-inline-edit-value">
        {selectedStudent.registrationDate ? new Date(selectedStudent.registrationDate).toLocaleDateString() : 'N/A'}
        {userRole === 'admin' && <PencilSquare className="stu-inline-pencil" onClick={() => setInlineEdit({ field: 'registrationDate', value: selectedStudent.registrationDate?.split('T')[0] || '' })} />}
      </span>
    )}
  </div>

  {/* ── Location ── */}
  <div className="stu-drawer-section-label">Address</div>

  <div className="stu-drawer-row">
    <span>Province</span>
    {userRole === 'admin' && inlineEdit.field === 'province' ? (
      <span className="stu-inline-edit-active">
        <select className="stu-inline-input" value={inlineEdit.value} onChange={e => setInlineEdit(p => ({ ...p, value: e.target.value }))}>
          <option value="">Select</option>
          {provinceOptions.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <button className="stu-inline-save" onClick={() => handleInlineSave('province')}>Save</button>
        <button className="stu-inline-cancel" onClick={() => setInlineEdit({ field: null, value: '' })}>Cancel</button>
      </span>
    ) : (
      <span className="stu-inline-edit-value">
        {selectedStudent.province || 'N/A'}
        {userRole === 'admin' && <PencilSquare className="stu-inline-pencil" onClick={() => setInlineEdit({ field: 'province', value: selectedStudent.province || '' })} />}
      </span>
    )}
  </div>

  <div className="stu-drawer-row">
    <span>Region</span>
    {userRole === 'admin' && inlineEdit.field === 'region' ? (
      <span className="stu-inline-edit-active">
        <select className="stu-inline-input" value={inlineEdit.value} onChange={e => setInlineEdit(p => ({ ...p, value: e.target.value }))}>
          <option value="">Select</option>
          {regionOptions.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        <button className="stu-inline-save" onClick={() => handleInlineSave('region')}>Save</button>
        <button className="stu-inline-cancel" onClick={() => setInlineEdit({ field: null, value: '' })}>Cancel</button>
      </span>
    ) : (
      <span className="stu-inline-edit-value">
        {selectedStudent.region || 'N/A'}
        {userRole === 'admin' && <PencilSquare className="stu-inline-pencil" onClick={() => setInlineEdit({ field: 'region', value: selectedStudent.region || '' })} />}
      </span>
    )}
  </div>

  <div className="stu-drawer-row">
    <span>Constituency</span>
    {userRole === 'admin' && inlineEdit.field === 'constituency' ? (
      <span className="stu-inline-edit-active">
        <select className="stu-inline-input" value={inlineEdit.value} onChange={e => setInlineEdit(p => ({ ...p, value: e.target.value }))}>
          <option value="">Select</option>
          {constituencyOptions.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <button className="stu-inline-save" onClick={() => handleInlineSave('constituency')}>Save</button>
        <button className="stu-inline-cancel" onClick={() => setInlineEdit({ field: null, value: '' })}>Cancel</button>
      </span>
    ) : (
      <span className="stu-inline-edit-value">
        {selectedStudent.constituency || 'N/A'}
        {userRole === 'admin' && <PencilSquare className="stu-inline-pencil" onClick={() => setInlineEdit({ field: 'constituency', value: selectedStudent.constituency || '' })} />}
      </span>
    )}
  </div>

  <div className="stu-drawer-row">
    <span>District</span>
    {userRole === 'admin' && inlineEdit.field === 'district' ? (
      <span className="stu-inline-edit-active">
        <input className="stu-inline-input" value={inlineEdit.value} onChange={e => setInlineEdit(p => ({ ...p, value: e.target.value }))} />
        <button className="stu-inline-save" onClick={() => handleInlineSave('district')}>Save</button>
        <button className="stu-inline-cancel" onClick={() => setInlineEdit({ field: null, value: '' })}>Cancel</button>
      </span>
    ) : (
      <span className="stu-inline-edit-value">
        {selectedStudent.district || 'N/A'}
        {userRole === 'admin' && <PencilSquare className="stu-inline-pencil" onClick={() => setInlineEdit({ field: 'district', value: selectedStudent.district || '' })} />}
      </span>
    )}
  </div>

  <div className="stu-drawer-row">
    <span>Village</span>
    {userRole === 'admin' && inlineEdit.field === 'village' ? (
      <span className="stu-inline-edit-active">
        <input className="stu-inline-input" value={inlineEdit.value} onChange={e => setInlineEdit(p => ({ ...p, value: e.target.value }))} />
        <button className="stu-inline-save" onClick={() => handleInlineSave('village')}>Save</button>
        <button className="stu-inline-cancel" onClick={() => setInlineEdit({ field: null, value: '' })}>Cancel</button>
      </span>
    ) : (
      <span className="stu-inline-edit-value">
        {selectedStudent.village || 'N/A'}
        {userRole === 'admin' && <PencilSquare className="stu-inline-pencil" onClick={() => setInlineEdit({ field: 'village', value: selectedStudent.village || '' })} />}
      </span>
    )}
  </div>

  {/* ── Family ── */}
  <div className="stu-drawer-section-label">Family</div>

  <div className="stu-drawer-row">
    <span>Father's Name</span>
    {userRole === 'admin' && inlineEdit.field === 'fatherName' ? (
      <span className="stu-inline-edit-active">
        <input className="stu-inline-input" value={inlineEdit.value} onChange={e => setInlineEdit(p => ({ ...p, value: e.target.value }))} />
        <button className="stu-inline-save" onClick={() => handleInlineSave('fatherName')}>Save</button>
        <button className="stu-inline-cancel" onClick={() => setInlineEdit({ field: null, value: '' })}>Cancel</button>
      </span>
    ) : (
      <span className="stu-inline-edit-value">
        {selectedStudent.fatherName || 'N/A'}
        {userRole === 'admin' && <PencilSquare className="stu-inline-pencil" onClick={() => setInlineEdit({ field: 'fatherName', value: selectedStudent.fatherName || '' })} />}
      </span>
    )}
  </div>

  <div className="stu-drawer-row">
    <span>Father's Occ.</span>
    {userRole === 'admin' && inlineEdit.field === 'fathersOccupation' ? (
      <span className="stu-inline-edit-active">
        <input className="stu-inline-input" value={inlineEdit.value} onChange={e => setInlineEdit(p => ({ ...p, value: e.target.value }))} />
        <button className="stu-inline-save" onClick={() => handleInlineSave('fathersOccupation')}>Save</button>
        <button className="stu-inline-cancel" onClick={() => setInlineEdit({ field: null, value: '' })}>Cancel</button>
      </span>
    ) : (
      <span className="stu-inline-edit-value">
        {selectedStudent.fathersOccupation || 'N/A'}
        {userRole === 'admin' && <PencilSquare className="stu-inline-pencil" onClick={() => setInlineEdit({ field: 'fathersOccupation', value: selectedStudent.fathersOccupation || '' })} />}
      </span>
    )}
  </div>

  <div className="stu-drawer-row">
    <span>Mother's Name</span>
    {userRole === 'admin' && inlineEdit.field === 'motherName' ? (
      <span className="stu-inline-edit-active">
        <input className="stu-inline-input" value={inlineEdit.value} onChange={e => setInlineEdit(p => ({ ...p, value: e.target.value }))} />
        <button className="stu-inline-save" onClick={() => handleInlineSave('motherName')}>Save</button>
        <button className="stu-inline-cancel" onClick={() => setInlineEdit({ field: null, value: '' })}>Cancel</button>
      </span>
    ) : (
      <span className="stu-inline-edit-value">
        {selectedStudent.motherName || 'N/A'}
        {userRole === 'admin' && <PencilSquare className="stu-inline-pencil" onClick={() => setInlineEdit({ field: 'motherName', value: selectedStudent.motherName || '' })} />}
      </span>
    )}
  </div>

  <div className="stu-drawer-row">
    <span>Mother's Occ.</span>
    {userRole === 'admin' && inlineEdit.field === 'mothersOccupation' ? (
      <span className="stu-inline-edit-active">
        <input className="stu-inline-input" value={inlineEdit.value} onChange={e => setInlineEdit(p => ({ ...p, value: e.target.value }))} />
        <button className="stu-inline-save" onClick={() => handleInlineSave('mothersOccupation')}>Save</button>
        <button className="stu-inline-cancel" onClick={() => setInlineEdit({ field: null, value: '' })}>Cancel</button>
      </span>
    ) : (
      <span className="stu-inline-edit-value">
        {selectedStudent.mothersOccupation || 'N/A'}
        {userRole === 'admin' && <PencilSquare className="stu-inline-pencil" onClick={() => setInlineEdit({ field: 'mothersOccupation', value: selectedStudent.mothersOccupation || '' })} />}
      </span>
    )}
  </div>

  <div className="stu-drawer-row">
    <span>Parents' Phone</span>
    {userRole === 'admin' && inlineEdit.field === 'parentsPhoneNumber' ? (
      <span className="stu-inline-edit-active">
        <input className="stu-inline-input" value={inlineEdit.value} onChange={e => setInlineEdit(p => ({ ...p, value: e.target.value }))} />
        <button className="stu-inline-save" onClick={() => handleInlineSave('parentsPhoneNumber')}>Save</button>
        <button className="stu-inline-cancel" onClick={() => setInlineEdit({ field: null, value: '' })}>Cancel</button>
      </span>
    ) : (
      <span className="stu-inline-edit-value">
        {selectedStudent.parentsPhoneNumber || 'N/A'}
        {userRole === 'admin' && <PencilSquare className="stu-inline-pencil" onClick={() => setInlineEdit({ field: 'parentsPhoneNumber', value: selectedStudent.parentsPhoneNumber || '' })} />}
      </span>
    )}
  </div>

  <div className="stu-drawer-row">
    <span>Student Name</span>
    {userRole === 'admin' && inlineEdit.field === 'studentName' ? (
      <span className="stu-inline-edit-active">
        <input className="stu-inline-input" value={inlineEdit.value} onChange={e => setInlineEdit(p => ({ ...p, value: e.target.value }))} />
        <button className="stu-inline-save" onClick={() => handleInlineSave('studentName')}>Save</button>
        <button className="stu-inline-cancel" onClick={() => setInlineEdit({ field: null, value: '' })}>Cancel</button>
      </span>
    ) : (
      <span className="stu-inline-edit-value">
        {selectedStudent.studentName || 'N/A'}
        {userRole === 'admin' && <PencilSquare className="stu-inline-pencil" onClick={() => setInlineEdit({ field: 'studentName', value: selectedStudent.studentName || '' })} />}
      </span>
    )}
  </div>

</div>
                </div>
              )}

            </div>
          </div>

          {/* Pagination */}
          {renderPagination()}

          {/* Action buttons */}
          <div className="stu-action-bar">
            <button className="stu-btn stu-btn-info" onClick={() => setShowPrintModal(true)}>
              <Printer /> Print by Class
            </button>
            <button className="stu-btn stu-btn-primary" onClick={() => setShowConstituencyPrintModal(true)}>
              <Printer /> Print by Constituency
            </button>
            <button className="stu-btn stu-btn-success" onClick={() => setShowRegionPrintModal(true)}>
              <Printer /> Print by Region
            </button>
            <button className="stu-btn stu-btn-warning" onClick={() => setShowReligionPrintModal(true)}>
              <Printer /> Print by Religion
            </button>
            <button
              className="stu-btn stu-btn-warning"
              onClick={handleShowStats}
              aria-controls="stu-stats-collapse"
              aria-expanded={showStats}
              disabled={exporting}
            >
              <BarChartFill /> {showStats ? 'Hide Statistics' : 'Show Statistics'}
            </button>
            {userRole === 'admin' && (
              <ExportStudentsDropdown
                exporting={exporting}
                setExporting={setExporting}
                showNotification={showNotification}
              />
            )}
          </div>

          {/* Statistics collapse */}
          <Collapse in={showStats}>
            <div id="stu-stats-collapse" className="stu-stats-panel">

              <h5 className="stu-stats-title">Student Statistics Overview</h5>

              {/* Overview cards */}
              <div className="stu-stats-overview">
                <div className="stu-stat-card stu-stat-card-primary">
                  <div className="stu-stat-card-header">Total Students</div>
                  <div className="stu-stat-card-body">
                    <div className="stu-stat-number">{statistics.totalStudents}</div>
                    <div className="stu-stat-sub">Registered Students</div>
                  </div>
                </div>
                <div className="stu-stat-card stu-stat-card-success">
                  <div className="stu-stat-card-header">Male Students</div>
                  <div className="stu-stat-card-body">
                    <div className="stu-stat-number">{statistics.maleStudents}</div>
                    <div className="stu-stat-sub">{((statistics.maleStudents / statistics.totalStudents) * 100 || 0).toFixed(1)}%</div>
                  </div>
                </div>
                <div className="stu-stat-card stu-stat-card-info">
                  <div className="stu-stat-card-header">Female Students</div>
                  <div className="stu-stat-card-body">
                    <div className="stu-stat-number">{statistics.femaleStudents}</div>
                    <div className="stu-stat-sub">{((statistics.femaleStudents / statistics.totalStudents) * 100 || 0).toFixed(1)}%</div>
                  </div>
                </div>
              </div>

              {/* Class-wise accordion */}
              <div className="stu-class-card">
                <div className="stu-class-card-header">Class-wise Statistics</div>
                <div className="stu-accordion" id="stuClassAccordion">
                  {classOptions.map(cls => {
                    const classData = statistics.classStats[cls] || { total: 0, male: 0, female: 0, studentNames: '' };
                    const studentList = classData.studentNames ? classData.studentNames.split(', ') : [];
                    return (
                      <div key={cls} className="stu-accordion-item">
                        <h2 className="stu-accordion-heading">
                          <button
                            className="stu-accordion-btn collapsed"
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target={`#stuClass${cls}`}
                          >
                            <span className="stu-acc-badge stu-acc-badge-primary">{classData.total}</span>
                            <span className="stu-acc-badge stu-acc-badge-warning">
                              Class {cls} — {classData.male} Male, {classData.female} Female
                            </span>
                          </button>
                        </h2>
                        <div id={`stuClass${cls}`} className="accordion-collapse collapse" data-bs-parent="#stuClassAccordion">
                          <div className="stu-accordion-body">
                            <strong>Students:</strong>
                            <div className="stu-student-list-container mt-2">
                              {studentList.length > 0
                                ? <ul className="stu-student-list">{studentList.map((s, i) => <li key={i} className="stu-student-list-item">{s}</li>)}</ul>
                                : <div className="stu-empty-text">No students in this class</div>}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Region / Religion / District cards */}
              <div className="stu-breakdown-grid">
                {/* Region */}
                <div className="stu-breakdown-card">
                  <div className="stu-breakdown-header stu-breakdown-info">Region Statistics</div>
                  <div className="stu-breakdown-body">
                    {Object.entries(statistics.regionStats || {}).map(([region, data]) => (
                      <div key={region} className="stu-breakdown-item">
                        <div className="stu-breakdown-row">
                          <span className="stu-breakdown-label">{region}</span>
                          <span className="stu-breakdown-badge stu-badge-primary">{data.count}</span>
                        </div>
                        <div className="stu-student-list-container mt-1">
                          {renderStudentList(data.studentNames)}
                        </div>
                      </div>
                    ))}
                    {Object.keys(statistics.regionStats || {}).length === 0 && (
                      <div className="stu-empty-text stu-p-3">No region data available</div>
                    )}
                  </div>
                </div>

                {/* Religion */}
                <div className="stu-breakdown-card">
                  <div className="stu-breakdown-header stu-breakdown-warning">Religion Statistics</div>
                  <div className="stu-breakdown-body">
                    {Object.entries(statistics.religionStats || {}).map(([religion, data]) => (
                      <div key={religion} className="stu-breakdown-item">
                        <div className="stu-breakdown-row">
                          <span className="stu-breakdown-label">{religion}</span>
                          <span className="stu-breakdown-badge stu-badge-warning">{data.count}</span>
                        </div>
                        <div className="stu-student-list-container mt-1">
                          {renderStudentList(data.studentNames)}
                        </div>
                      </div>
                    ))}
                    {Object.keys(statistics.religionStats || {}).length === 0 && (
                      <div className="stu-empty-text stu-p-3">No religion data available</div>
                    )}
                  </div>
                </div>

                {/* District */}
                <div className="stu-breakdown-card">
                  <div className="stu-breakdown-header stu-breakdown-secondary">District Statistics</div>
                  <div className="stu-breakdown-body">
                    {Object.entries(statistics.districtStats || {}).map(([district, data]) => (
                      <div key={district} className="stu-breakdown-item">
                        <div className="stu-breakdown-row">
                          <span className="stu-breakdown-label">{district}</span>
                          <span className="stu-breakdown-badge stu-badge-secondary">{data.count}</span>
                        </div>
                        <div className="stu-student-list-container mt-1">
                          {renderStudentList(data.studentNames)}
                        </div>
                      </div>
                    ))}
                    {Object.keys(statistics.districtStats || {}).length === 0 && (
                      <div className="stu-empty-text stu-p-3">No district data available</div>
                    )}
                  </div>
                </div>
              </div>

            </div>
          </Collapse>

          {/* Delete Modal */}
          <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} className="stu-modal">
            <Modal.Header closeButton className="stu-modal-header">
              <Modal.Title className="stu-modal-title">Confirm Deletion</Modal.Title>
            </Modal.Header>
            <Modal.Body className="stu-modal-body">Are you sure you want to delete this student?</Modal.Body>
            <Modal.Footer className="stu-modal-footer">
              <button className="stu-btn stu-btn-secondary" onClick={() => setShowDeleteModal(false)}>Cancel</button>
              <button className="stu-btn stu-btn-danger" onClick={confirmDelete}>Delete</button>
            </Modal.Footer>
          </Modal>

          {/* Add / Edit Form — admin only */}
          {userRole === 'admin' && (
            <>
              <h4 ref={formRef} className="stu-section-title stu-form-title">
                {editingStudentId ? 'Edit Student' : 'Add Student'}
              </h4>
            {/* ── Student ID Banner ── */}
            <div className="stu-id-preview-banner">
              <div className="stu-id-preview-label">
                {editingStudentId ? 'Student ID Number' : 'Student ID'}
              </div>
              {editingStudentId ? (
                <div className="stu-id-preview-note stu-id-preview-note--permanent">
                  {editingStudentName
                    ? <><strong>{editingStudentName}</strong> already has a permanent unique Student ID number.</>
                    : 'This student already has a permanent unique Student ID number.'
                  }
                </div>
              ) : (
                <div className="stu-id-preview-note">
                  A unique Student ID will be automatically generated when the student is added.
                  You can view it in the student table after submission.
                </div>
              )}
            </div>
              <form onSubmit={handleSubmit} className="stu-add-form">
                <div className="stu-form-grid">
                  {/* Left column */}
                  <div className="stu-form-col">
                    <FormField id="formStudentName" label="Student Name" error={validationErrors.studentName}>
                      <input type="text" className={`stu-form-input${validationErrors.studentName ? ' stu-input-invalid' : ''}`} name="studentName" placeholder=" " value={studentData.studentName} onChange={handleChange} />
                    </FormField>
                    <FormField id="formEmail" label="Email" error={validationErrors.email}>
                      <input type="email" className={`stu-form-input${validationErrors.email ? ' stu-input-invalid' : ''}`} name="email" placeholder=" " value={studentData.email} onChange={handleChange} />
                    </FormField>
                    <FormField id="formDateOfBirth" label="Date of Birth" error={validationErrors.dateOfBirth} isActive={true}>
                    <DatePicker
                        key={`dob-${formResetKey}`}
                        name="dateOfBirth"
                        value={studentData.dateOfBirth}
                        onChange={handleChange}
                        isInvalid={!!validationErrors.dateOfBirth}
                        maxYear={new Date().getFullYear()}
                        minYear={new Date().getFullYear() - 100}
                      />
                  </FormField>
                    <FormField id="formGender" label="Gender" error={validationErrors.gender}>
                      <select className={`stu-form-input${validationErrors.gender ? ' stu-input-invalid' : ''}`} name="gender" value={studentData.gender} onChange={handleChange}>
                        <option value="">Select Gender</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                      </select>
                    </FormField>
                    <FormField id="formLastSchool" label="Last School Attended" error={validationErrors.lastSchoolAttended}>
                      <input type="text" className={`stu-form-input${validationErrors.lastSchoolAttended ? ' stu-input-invalid' : ''}`} name="lastSchoolAttended" placeholder=" " value={studentData.lastSchoolAttended} onChange={handleChange} />
                    </FormField>
                 <FormField label="Province" error={validationErrors.province} isActive={!!studentData.province || provinceOpen}>
                    <SearchableSelect name="province" value={studentData.province} onChange={handleChange} options={provinceOptions} placeholder=" " isInvalid={!!validationErrors.province} onOpenChange={setProvinceOpen}
                    onNewOption={(val) => dynamicOptionsAPI.saveOption('province', val).then(() => setProvinceOptions(prev => [...prev, val].sort()))} />
                  </FormField>
                  <FormField label="Region" error={validationErrors.region} isActive={!!studentData.region || regionOpen}>
                    <SearchableSelect name="region" value={studentData.region} onChange={handleChange} options={regionOptions} placeholder=" " isInvalid={!!validationErrors.region} onOpenChange={setRegionOpen}
  onNewOption={(val) => dynamicOptionsAPI.saveOption('region', val).then(() => setRegionOptions(prev => [...prev, val].sort()))} />
                  </FormField>
                  <FormField label="Religion" error={validationErrors.religion} isActive={!!studentData.religion || religionOpen}>
                    <SearchableSelect name="religion" value={studentData.religion} onChange={handleChange} options={religionOptions} placeholder=" " isInvalid={!!validationErrors.religion} onOpenChange={setReligionOpen}
  onNewOption={(val) => dynamicOptionsAPI.saveOption('religion', val).then(() => setReligionOptions(prev => [...prev, val].sort()))} />
                  </FormField>
                  <FormField label="Constituency" error={validationErrors.constituency} isActive={!!studentData.constituency || constituencyOpen}>
                    <SearchableSelect name="constituency" value={studentData.constituency} onChange={handleChange} options={constituencyOptions} placeholder=" " isInvalid={!!validationErrors.constituency} onOpenChange={setConstituencyOpen}
  onNewOption={(val) => dynamicOptionsAPI.saveOption('constituency', val).then(() => setConstituencyOptions(prev => [...prev, val].sort()))} />
                  </FormField>
                    <FormField id="formDistrict" label="District" error={validationErrors.district}>
                      <input type="text" className={`stu-form-input${validationErrors.district ? ' stu-input-invalid' : ''}`} name="district" placeholder=" " value={studentData.district} onChange={handleChange} />
                    </FormField>
                    <FormField id="formFathersOccupation" label="Father's Occupation" error={validationErrors.fathersOccupation}>
                      <input type="text" className={`stu-form-input${validationErrors.fathersOccupation ? ' stu-input-invalid' : ''}`} name="fathersOccupation" placeholder=" " value={studentData.fathersOccupation} onChange={handleChange} />
                    </FormField>
                  </div>

                  {/* Right column */}
                  <div className="stu-form-col">
                    <FormField id="formVillage" label="Village" error={validationErrors.village}>
                      <input type="text" className={`stu-form-input${validationErrors.village ? ' stu-input-invalid' : ''}`} name="village" placeholder=" " value={studentData.village} onChange={handleChange} />
                    </FormField>
                    <FormField id="formClass" label="Class" error={validationErrors.class}>
                      <select className={`stu-form-input${validationErrors.class ? ' stu-input-invalid' : ''}`} name="class" value={studentData.class} onChange={handleChange}>
                        <option value="">Select Class</option>
                        {classOptions.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </FormField>
                    <FormField id="formPhoneNumber" label="Phone Number" error={validationErrors.phoneNumber}>
                      <input type="text" className={`stu-form-input${validationErrors.phoneNumber ? ' stu-input-invalid' : ''}`} name="phoneNumber" placeholder=" " value={studentData.phoneNumber} onChange={handleChange} />
                    </FormField>
                    <FormField id="formRegistrationDate" label="Registration Date" error={validationErrors.registrationDate} isActive={true}>
                      <DatePicker
                            key={`reg-${formResetKey}`}
                            name="registrationDate"
                            value={studentData.registrationDate}
                            onChange={handleChange}
                            isInvalid={!!validationErrors.registrationDate}
                            maxYear={new Date().getFullYear()}
                            minYear={new Date().getFullYear() - 10}
                          />
                    </FormField>
                    <FormField id="formFatherName" label="Father's Name" error={validationErrors.fatherName}>
                      <input type="text" className={`stu-form-input${validationErrors.fatherName ? ' stu-input-invalid' : ''}`} name="fatherName" placeholder=" " value={studentData.fatherName} onChange={handleChange} />
                    </FormField>
                    <FormField id="formMotherName" label="Mother's Name" error={validationErrors.motherName}>
                      <input type="text" className={`stu-form-input${validationErrors.motherName ? ' stu-input-invalid' : ''}`} name="motherName" placeholder=" " value={studentData.motherName} onChange={handleChange} />
                    </FormField>
                    <FormField id="formMothersOccupation" label="Mother's Occupation" error={validationErrors.mothersOccupation}>
                      <input type="text" className={`stu-form-input${validationErrors.mothersOccupation ? ' stu-input-invalid' : ''}`} name="mothersOccupation" placeholder=" " value={studentData.mothersOccupation} onChange={handleChange} />
                    </FormField>
                    <FormField id="formParentsPhone" label="Parents Phone Number" error={validationErrors.parentsPhoneNumber}>
                      <input type="text" className={`stu-form-input${validationErrors.parentsPhoneNumber ? ' stu-input-invalid' : ''}`} name="parentsPhoneNumber" placeholder=" " value={studentData.parentsPhoneNumber} onChange={handleChange} />
                    </FormField>
                    <div className="stu-use-id-password-toggle">
                      <label className="stu-toggle-label">
                        <input
                          type="checkbox"
                          className="stu-toggle-checkbox"
                          checked={useIdAsPassword}
                          onChange={handleUseIdAsPassword}
                          disabled={false}
                        />
                        <span className="stu-toggle-text">
                          Use Student ID as password
                        </span>
                      </label>
                    </div>
                    {!useIdAsPassword && (
                        <div className="stu-field-group">
                          <div className="stu-password-wrapper">
                            <input
                              type={showPassword ? 'text' : 'password'}
                              className={`stu-form-input stu-password-input${validationErrors.password ? ' stu-input-invalid' : ''}`}
                              name="password"
                              placeholder=" "
                              value={studentData.password}
                              onChange={handleChange}
                              autoComplete="new-password"
                              readOnly
                              onFocus={(e) => e.target.removeAttribute('readonly')}
                              data-has-value={studentData.password ? 'true' : 'false'}
                            />
                            <button type="button" className="stu-password-toggle" onClick={() => setShowPassword(!showPassword)}>
                              {showPassword ? <EyeSlash /> : <Eye />}
                            </button>
                          </div>
                          <label className={`stu-field-label${studentData.password ? ' stu-field-label--float' : ''}`}>Password</label>
                          {validationErrors.password && <div className="stu-field-error">{validationErrors.password}</div>}
                        </div>
                      )}
                  </div>
                </div>

                <div className="stu-form-submit-row">
                  <button type="submit" className="stu-btn stu-btn-success stu-btn-submit">
                    {editingStudentId ? 'Save Changes' : 'Add Student'}
                  </button>
                </div>
              </form>
            </>
          )}

        </div>
      </div>
      </div>

      {/* Print Modals */}
      <PrintClassModal show={showPrintModal} onHide={() => setShowPrintModal(false)} />
      <PrintStudentsModal show={showConstituencyPrintModal} onHide={() => setShowConstituencyPrintModal(false)} category="constituency" />
      <PrintStudentsModal show={showRegionPrintModal} onHide={() => setShowRegionPrintModal(false)} category="region" />
      <PrintStudentsModal show={showReligionPrintModal} onHide={() => setShowReligionPrintModal(false)} category="religion" />

      {/* Toast */}
      <div className={`stu-toast stu-toast-${toast.variant}${toast.show ? ' stu-toast-show' : ''}`}>
        <div className="stu-toast-header">
          <strong className="stu-toast-title">
            {toast.variant === 'success' ? 'Success' : 'Error'}
          </strong>
          <button className="stu-toast-close" onClick={() => setToast(prev => ({ ...prev, show: false }))}>×</button>
        </div>
        <div className="stu-toast-body">
          {toast.variant === 'success'
            ? <CheckCircleFill className="stu-toast-icon" />
            : <ExclamationTriangleFill className="stu-toast-icon" />}
          {toast.message}
        </div>
      </div>
        <StudentGuideModal />
    </>
  );
};

export default Students;
